<template>
  <PInputTextStyles />
  <PChipStyles />
  <Accordion
    :class="{ 'p-accordion--transparent': transparent }"
    v-bind="$attrs"
  >
    <template v-for="(_, slotName) in $slots" #[slotName]="slotProps">
      <slot :name="slotName" v-bind="slotProps ?? {}" />
    </template>
  </Accordion>
</template>

<script setup lang="ts">
import type { AccordionEmits, AccordionProps } from "primevue";

import { Accordion } from "primevue";

import PChipStyles from "./PChipStyles.vue";
import PInputTextStyles from "./PInputTextStyles.vue";

defineOptions({ inheritAttrs: false });
/* oxlint-disable-next-line perfectionist/sort-intersection-types */
defineProps<{ transparent?: boolean } & /* @vue-ignore */ AccordionProps>();
defineEmits</* @vue-ignore */ AccordionEmits>();
</script>

<style lang="postcss">
.p-accordion {
  @apply flex flex-col gap-2;

  &--transparent {
    .p-accordionpanel {
      @apply rounded-none border-0;
    }

    .p-accordionheader,
    .p-accordionpanel:not(.p-accordionpanel-active, .p-disabled)
      > .p-accordionheader:hover,
    .p-accordionpanel:not(.p-disabled).p-accordionpanel-active
      > .p-accordionheader,
    .p-accordionpanel:not(.p-disabled).p-accordionpanel-active
      > .p-accordionheader:hover {
      @apply bg-transparent text-inherit;
    }

    .p-accordionheader-toggle-icon,
    .p-accordionpanel:not(.p-accordionpanel-active, .p-disabled)
      .p-accordionheader:hover
      .p-accordionheader-toggle-icon,
    .p-accordionpanel:not(.p-disabled).p-accordionpanel-active
      > .p-accordionheader
      .p-accordionheader-toggle-icon {
      @apply text-inherit;
    }

    .p-accordioncontent-content {
      @apply bg-transparent text-inherit;
    }
  }
}

.p-accordionpanel {
  @apply flex flex-col rounded-md border-2 border-slate-100;

  &:first-child > .p-accordionheader {
    @apply rounded-se-md rounded-ss-md;
  }

  &:last-child > .p-accordionheader {
    @apply rounded-ee-md rounded-es-md;
  }

  &:last-child.p-accordionpanel-active > .p-accordionheader {
    @apply rounded-ee-md rounded-es-md;
  }

  &:not(.p-disabled) .p-accordionheader:focus-visible {
    @apply outline outline-1 outline-offset-[-1px] outline-primary;
  }

  &:not(.p-accordionpanel-active, .p-disabled) > .p-accordionheader:hover {
    @apply bg-surface-0 text-slate-700;
  }

  &:not(.p-accordionpanel-active, .p-disabled)
    .p-accordionheader:hover
    .p-accordionheader-toggle-icon {
    @apply text-slate-700;
  }

  &:not(.p-disabled).p-accordionpanel-active > .p-accordionheader {
    @apply bg-surface-0 text-slate-700;
  }

  &:not(.p-disabled).p-accordionpanel-active
    > .p-accordionheader
    .p-accordionheader-toggle-icon {
    @apply text-slate-700;
  }

  &:not(.p-disabled).p-accordionpanel-active > .p-accordionheader:hover {
    @apply bg-surface-0 text-slate-700;
  }

  &:not(.p-disabled).p-accordionpanel-active
    > .p-accordionheader:hover
    .p-accordionheader-toggle-icon {
    @apply text-slate-700;
  }
}

.p-accordionheader {
  @apply flex cursor-pointer items-start justify-between p-[1.125rem] font-semibold text-slate-500 transition-colors duration-200 disabled:cursor-auto;

  &-toggle-icon {
    @apply text-surface-500;
  }
}

.p-accordioncontent-content {
  @apply bg-surface-0 px-[1.125rem] pb-[1.125rem] pt-0 text-slate-700;
}
</style>
